
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { GlowingEffect } from "./glowing-effect"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:scale-105 active:scale-95",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        subtle: "bg-foreground/10 text-foreground border border-foreground/10 hover:bg-foreground/20",
        link: "text-primary underline-offset-4 hover:underline",
        rainbow: "group relative animate-rainbow cursor-pointer border-0 bg-[length:200%] [background-clip:padding-box,border-box,border-box] [background-origin:border-box] [border:calc(0.08*1rem)_solid_transparent] bg-[linear-gradient(#121213,#121213),linear-gradient(#121213_50%,rgba(18,18,19,0.6)_80%,rgba(18,18,19,0)),linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] dark:bg-[linear-gradient(#fff,#fff),linear-gradient(#fff_50%,rgba(255,255,255,0.6)_80%,rgba(0,0,0,0)),linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] text-primary-foreground before:absolute before:bottom-[-20%] before:left-1/2 before:z-0 before:h-1/5 before:w-3/5 before:-translate-x-1/2 before:animate-rainbow before:bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] before:bg-[length:200%] before:[filter:blur(calc(0.8*1rem))]",
      },
      size: {
        default: "h-8 px-3 py-1.5 text-xs sm:text-sm",
        sm: "h-7 sm:h-8 rounded-md px-2 sm:px-2.5 text-xs",
        lg: "h-9 sm:h-10 rounded-md px-4 sm:px-6 text-xs sm:text-sm",
        icon: "h-8 w-8 sm:h-9 sm:w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  rainbow?: boolean
  noGlow?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, rainbow = false, noGlow = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // Use rainbow variant if rainbow prop is true
    const finalVariant = rainbow ? "rainbow" : variant
    
    if (rainbow || noGlow) {
      return (
        <Comp
          className={cn(buttonVariants({ variant: finalVariant, size, className }))}
          ref={ref}
          style={rainbow ? {
            '--speed': '2s'
          } as React.CSSProperties : undefined}
          {...props}
        />
      )
    }

    return (
      <div className="relative inline-flex overflow-hidden rounded-md">
        <GlowingEffect
          spread={40}
          glow={true}
          disabled={false}
          proximity={80}
          inactiveZone={0.01}
          borderWidth={2}
          movementDuration={1.5}
          className="absolute inset-0 rounded-md pointer-events-none"
        />
        <Comp
          className={cn(buttonVariants({ variant: finalVariant, size, className }), "relative z-10")}
          ref={ref}
          {...props}
        />
      </div>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
